// Player preferences kept on this device (Settings › Preferences). They shape the chrome, so they're applied on <html>.
import { useEffect } from "react";
import { create } from "zustand";
import type { LabelPref, OpensAt } from "@/game/cameraMath";
import { isCitySort, type CitySort } from "@/game/cityList";

export interface Prefs {
  bannerPadding: boolean;
  tight: boolean;
  music: boolean;
  sfx: boolean;
  debug: boolean;
  /** recruit screen: play the unit's clip when it comes into view (off = the paintings) */
  recruitVideo: boolean;
  /** text size in percent, TEXT_SIZES (Settings › Display) */
  textSize: number;
  /** EB Garamond + Cinzel everywhere instead of Source Sans 3 for the small type */
  classicFonts: boolean;
  /** the city view: where it opens (Settings › Display › City view opens at) */
  cityOpens: OpensAt;
  /** the city view: on-building labels at the whole-city view (full / compact / level only) */
  cityLabels: LabelPref;
  /** the city switcher's order (its Sort control): name, coords, region, tag, Size (High) (most points first) or Size (Low) (fewest first) */
  citySort: CitySort;
  /** the city switcher's "Incoming" button: soonest landing first, grouped (Ben, Oct 14 2026); shown only while
   *  something is incoming, and the Sort above still orders the clear cities */
  cityIncomingSort: boolean;
}

/** Text size steps offered by Settings › Display. */
export const TEXT_SIZES = [85, 90, 95, 100, 105, 110, 115, 120, 125, 130] as const;
export const clampTextSize = (n: number) => {
  const v = Math.round(Number(n) / 5) * 5;
  return Number.isFinite(v) ? Math.min(130, Math.max(85, v)) : 100;
};

const KEY = "valor-web:prefs";
const DEFAULTS: Prefs = { bannerPadding: true, tight: false, music: false, sfx: false, debug: false, recruitVideo: true, textSize: 100, classicFonts: false, cityOpens: "last", cityLabels: "compact", citySort: "name", cityIncomingSort: false };
const OPENS: OpensAt[] = ["last", "overview", "whole", "close"];
const LABELS: LabelPref[] = ["full", "compact", "level"];

function load(): Prefs {
  try {
    const p = { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY) || "{}") as Partial<Prefs>) };
    return {
      ...p,
      textSize: clampTextSize(p.textSize),
      cityOpens: OPENS.includes(p.cityOpens) ? p.cityOpens : DEFAULTS.cityOpens,
      cityLabels: LABELS.includes(p.cityLabels) ? p.cityLabels : DEFAULTS.cityLabels,
      citySort: isCitySort(p.citySort) ? p.citySort : DEFAULTS.citySort,
      cityIncomingSort: p.cityIncomingSort === true,
    };
  } catch {
    return DEFAULTS;
  }
}

interface PrefState extends Prefs {
  set: (patch: Partial<Prefs>) => void;
}

export const usePrefs = create<PrefState>((set, get) => ({
  ...load(),
  set: (patch) => {
    set(patch);
    const { set: _s, ...p } = get();
    void _s;
    try {
      localStorage.setItem(KEY, JSON.stringify({ ...p, ...patch }));
    } catch {
      /* private mode: the choice lasts for this visit */
    }
  },
}));

// Before the first paint: the saved text size and faces, so the app doesn't open at 100% and then jump.
if (typeof document !== "undefined") {
  const p = usePrefs.getState();
  document.documentElement.classList.toggle("classic-fonts", p.classicFonts);
  document.documentElement.classList.toggle("tz-big", clampTextSize(p.textSize) >= 115);
  document.documentElement.style.setProperty("--tz", String(clampTextSize(p.textSize) / 100));
}

/** Puts the preferences on <html> as classes / variables so the kit can react. */
export function useApplyPrefs() {
  const tight = usePrefs((s) => s.tight);
  const banner = usePrefs((s) => s.bannerPadding);
  const textSize = usePrefs((s) => s.textSize);
  const classic = usePrefs((s) => s.classicFonts);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("tight", tight);
    root.classList.toggle("no-banner", !banner);
    root.classList.toggle("classic-fonts", classic);
    root.classList.toggle("tz-big", clampTextSize(textSize) >= 115);
    // Tight interface is spacing only; the text size is its own setting.
    root.style.setProperty("--tz", String(clampTextSize(textSize) / 100));
  }, [tight, banner, textSize, classic]);
}
